"use client";

import React, { ReactNode, useEffect, useMemo, useRef } from "react";
import { usePathname } from "next/navigation";
import { gsap } from "../../lib/gsap";

type Props = { children: ReactNode };

// Toggle transition style here:
const MODE: "curtain" | "blur" = "curtain";

export default function PageTransition({ children }: Props) {
  const pathname = usePathname();
  const currRef = useRef<HTMLDivElement>(null);
  const curtainRef = useRef<HTMLDivElement>(null);
  const hasMountedRef = useRef(false);

  const current = useMemo(
    () => (
      <div
        key={pathname}
        ref={currRef}
        className="pt__current will-change-transform will-change-opacity"
      >
        {children}
      </div>
    ),
    [pathname, children],
  );

  useEffect(() => {
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;
    const $curtain = curtainRef.current;
    const $curr = currRef.current;
    if (!$curr) return;

    // NEW: detect first load (we'll animate instead of skipping)
    const isFirstLoad = !hasMountedRef.current;
    hasMountedRef.current = true;

    // Reduced motion: just show
    if (reduce) {
      gsap.set($curr, { opacity: 1, y: 0, filter: "blur(0px)" });
      if ($curtain) gsap.set($curtain, { scaleY: 0, opacity: 1 });
      return;
    }

    // Scroll to top BEFORE showing new page
    const resetScroll = () => {
      // Supports GSAP ScrollSmoother if present
      // @ts-ignore
      const smoother = (gsap as any).ScrollSmoother?.get?.();
      if (smoother) {
        smoother.scrollTo(0, true);
      } else {
        window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
      }
    };

    if (MODE === "curtain") {
      if (!$curtain) return;

      const tl = gsap.timeline({ defaults: { ease: "power2.out" } });

      if (isFirstLoad) {
        // NEW: initial load sequence (start covered -> reveal)
        gsap.set($curtain, {
          transformOrigin: "50% 0%",
          scaleY: 1,
          opacity: 1,
        });
        gsap.set($curr, { opacity: 0, filter: "blur(6px)" });

        tl.set($curtain, { transformOrigin: "50% 100%" })
          .to($curtain, { scaleY: 0, duration: 0.45 })
          .to($curr, { opacity: 1, filter: "blur(0px)", duration: 0.45 }, "<");

        return () => {
          tl.kill();
        };
      }

      // EXISTING: route-change sequence (cover -> scrollTop -> uncover + reveal)
      gsap.set($curtain, {
        transformOrigin: "50% 0%", // from TOP
        scaleY: 0,
        opacity: 1,
      });
      gsap.set($curr, { opacity: 0, filter: "blur(6px)" });

      tl.to($curtain, { scaleY: 1, duration: 0.35 })
        .add(() => resetScroll())
        .set($curtain, { transformOrigin: "50% 100%" }) // collapse towards BOTTOM
        .to($curtain, { scaleY: 0, duration: 0.45 })
        .to($curr, { opacity: 1, filter: "blur(0px)", duration: 0.45 }, "<");

      return () => {
        tl.kill();
      };
    } else {
      // BLUR mode
      const tl = gsap.timeline({ defaults: { ease: "power2.out" } });

      if (isFirstLoad) {
        // NEW: initial load (no scroll reset)
        gsap.set($curr, { opacity: 0, filter: "blur(6px)" });
        tl.to($curr, { opacity: 1, filter: "blur(0px)", duration: 0.45 });
        return () => {
          tl.kill();
        };
      }

      // EXISTING: route-change blur crossfade
      gsap.set($curr, { opacity: 0, filter: "blur(6px)" });
      resetScroll();
      tl.to($curr, { opacity: 1, filter: "blur(0px)", duration: 0.45 });

      return () => {
        tl.kill();
      };
    }
  }, [pathname]);

  return (
    <div className="relative">
      {current}

      {/* Curtain overlay only used in MODE === "curtain" */}
      <div
        ref={curtainRef}
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[9999]"
        style={{
          display: MODE === "curtain" ? "block" : "none",
          // Elegant white gradient
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.86) 0%, rgba(255,255,255,0.96) 50%, rgba(255,255,255,0.75) 100%)",
          transform: "scaleY(0)",
          transformOrigin: "50% 0%",
        }}
      />
    </div>
  );
}
